import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import formatFns from 'date-fns/format';
import parseFns from 'date-fns/parse';
import add from 'date-fns/add';
import addDaysFns from 'date-fns/addDays';
import { TDateRange } from '@types';

type ObjectType = {
	[key:string]: string;
}

export const DATE_FORMATS:ObjectType = {
	API_DATE_FORMAT: 'yyyy-MM-dd',
	SHORT_DATE: 'dd/MM/yyyy',
	DATE_WITH_SHORT_MONTH_NAME: 'dd MMM yyyy',
	DATE_WITH_SHORT_MONTH_NAME_NO_PAD: 'd MMM yyyy',
	DATE_WITH_FULL_MONTH_NAME: 'dd MMMM yyyy',
	DATE_WITH_SHORT_MONTH_TIME: 'dd MMM yyyy HH:mm',
	ISO: 'ISO',
	US_DATE_WITH_SHORT_MONTH_NAME: 'MMM dd, yyyy',
	US_TEXT: 'MMM, do yy',
	AU_LONG_DATE_AND_TIME: 'dd-MMM-yyyy HH:mm:ss',
	API_TIMESTAMP_FORMAT: 'yyyy-MM-dd kk:mm:ss',
	SHORT_DATE_NO_YEAR: 'dd MMM',
	FULL_DATE_WITH_DAY: 'EEEE do MMMM yyyy'
}

const formatFromName = (name:string) => {
	return DATE_FORMATS[name] || name
}

/**
 * @param {string} [dateString]
 * @param { keyof DATE_FORMATS } [format]
 */
export const parseDateString = (dateString: string, format='API_DATE_FORMAT') => {
	const formatPattern = formatFromName(format);
	if (!dateString) {
		return new Date(NaN);
	}

	if (format === DATE_FORMATS.ISO) {
		return parseISO(dateString);
	}

	return parseFns(dateString, formatPattern, new Date());
}

/**
 * @param {Date} date
 * @param {keyof DATE_FORMATS} [format]
 */
export const formatDate = (date: Date, format= 'SHORT_DATE') => {
	const formatPattern = formatFromName(format);

	if (typeof date !== 'object') {
		console.error(`Invalid date in formatDate: ${date}`)
		return '';
	}

	if (isNaN(date.getTime())) {
		return ''
	}

	if (format === DATE_FORMATS.ISO) {
		return formatISO(date);
	}

	return formatFns(date, formatPattern);
}

export const convertFromAus = (date: string) => {
	if (date && typeof date === 'string' && date.indexOf('/') >= 0) {
		const parts = date.split('/').reverse();
		return parts.join('-');
	}
	return date;
}

export const getTime = (date: Date, as24 = true, returnParts = false, leadingZero = false) => {
	const theDate = new Date(date);
	const Hour = theDate.getHours();
	const Hour12 = Hour > 12 ? Hour - 12 : Hour;
	const Min = theDate.getMinutes();
	const Period = Hour < 12 ? 'am' : 'pm';
	if (returnParts) {
		return {
			hour: Hour,
			min: Min,
			period: Period
		}
	}
	let hour: number | string = Hour;
	if (leadingZero) hour = Hour.toString().padStart(2, '0');
	return `${as24 ? hour : Hour12}:${Min.toString().padStart(2, '0')}${as24 ? '' : ` ${Period}`}`;
}

export const formatTimeFromString = (inputTime:string, returnPeriod = true) => {
	const time = `${inputTime}`; // --> format to string... just in case...
	const [hour, min] = time.split(':');
	const Hour12 = parseInt(hour) > 12 ? parseInt(hour) - 12 : parseInt(hour);
	const Period = parseInt(hour) < 12 ? 'am' : 'pm';
	return `${Hour12}:${min}${returnPeriod ? Period : ''}`;
}

export const getDateFromTime = (date:Date, time:string) => {
	if (date && time) {
		const [hour, min] = time.split(':');
		const year = date.getFullYear();
		const month = date.getMonth();
		const day = date.getDate();
		return new Date(year, month, day, parseInt(hour), parseInt(min));
	}
	return null;
}

export const convertAusDate = (dateString:string) => {
	const dateParts = dateString.split('/');
	if (dateParts.length < 3) return null;
	return new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
}

/**
 * @param {string} [dateString]
 * @param {keyof DATE_FORMATS} [outputFormat]
 * @param {keyof DATE_FORMATS} [inputFormat]
 * @param {string} [invalidDateString]
 */
export const parseAndFormatDate = (dateString: string, outputFormat='SHORT_DATE', inputFormat='API_DATE_FORMAT', invalidDateString='') => {
	const date = parseDateString(dateString, inputFormat);
	if (isNaN(date.getTime())) {
		return invalidDateString;
	}

	return formatDate(date, outputFormat)
}

export const getDayName = (date:string) => {
	const theDate = new Date(date);
	const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	const day = theDate.getDay();
	return days[day];
}

export const formatDateOz = (dateString: Date, format = 'Do MMM YYYY') => {
	return formatDate(dateString, format);
}

export const formatTime = (value: Date, format = 'h:mma') => {
	return formatDate(value, format);
}

export const addToTime = (time: string, hours: number = 0, minutes: number = 0, seconds: number = 0) => {
	const [hour, min] = time.split(':');
	const now = new Date();
	const date = new Date(now.getFullYear(), now.getMonth() -1, now.getDate(), parseInt(hour), parseInt(min));
	const result = add(date, {
		hours,
		minutes,
		seconds
	});
	const addLeadingZero = (n:number) => n <= 9 ? `0${n}` : `${n}`;
	return `${addLeadingZero(result.getHours())}:${addLeadingZero(result.getMinutes())}:${addLeadingZero(result.getSeconds())}`;
}


export const normalizeISODate = (rawDate: string ) => {
	const date = parseDateString(rawDate, 'ISO');
	const userTimezoneOffset = date.getTimezoneOffset() * 60000;
	return new Date(date.getTime() + userTimezoneOffset);
}


export const formatIsoDateTimeToSydneyDate = (dateTimeString: string): string => {
	const isoDateTime = new Date(dateTimeString);
	return new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', }).format(isoDateTime);
}

export const formatIsoDateTimeToSydneyTime = (dateTimeString: string): string => {
	const isoDateTime = new Date(dateTimeString);
	return new Intl.DateTimeFormat(
		'en-AU', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: false,
			timeZone: 'Australia/Sydney'
		}).format(isoDateTime);


}

export const formatDateStringToAuLongDateString = (dateString: string): string => {
	const [day, month, year] = dateString.split('/').map(Number);
	const date = new Date(year, month - 1, day);
	return date.toLocaleDateString('en-AU', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	});
}

export const timeFormat12Hours = (time: string) => {
	const militaryTime =time.split(':');
	const hours = parseInt(militaryTime[0], 10);
	let period = 'AM';
	if (hours >= 12) {
		period = 'PM';
	}
	let hours12 = hours % 12;
	hours12 = hours12 ? hours12 : 12; // Handle midnight (00:00) case
	return `${hours12} ${period}`
}

export const dateFromISOint = (date: number) => {
	if (`${date}`.length !== 8) return null;
	const dateString = `${date}`.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
	return dateString;
}

export const timeAgo = (timestamp: number): string => {
	const mins = Math.floor((Date.now() - timestamp) / 60000);
	if (mins < 1) return 'just now';
	if (mins < 60) return `${mins}min${mins === 1 ? '' : 's'} ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}hr${hrs === 1 ? '' : 's'} ago`;
	const days = Math.floor(hrs / 24);
	if (days < 7) return `${days}day${days === 1 ? '' : 's'} ago`;
	const wks = Math.floor(days / 7);
	return `${wks}wk${wks === 1 ? '' : 's'} ago`;
}

export const isDate = (date: any) => {
	if (Object.prototype.toString.call(date) === '[object Date]') {
		return !isNaN(date.valueOf());
	}
	return false;
}

/* Narrows a filter value to a date range ({ start, end }). After a URL/JSON round-trip the
   start/end may be strings rather than Dates (see parseRoundTrippedDate), so only the keys are
   checked here. */
export const isDateRange = (value: any): value is TDateRange => {
	return !!value
		&& typeof value === 'object'
		&& !Array.isArray(value)
		&& !isDate(value)
		&& 'start' in value
		&& 'end' in value;
}

export const isStringValidDate = (dateString: string) => {
	const date = new Date(dateString);
	return !isNaN(date.getTime());
}

/* Filter values persisted via dateReplacer (see Filter.controller.ts) round-trip through the URL
   as 'dd/MM/yyyy' strings, not ISO. The generic Date constructor assumes MM/DD/YYYY for
   slash-delimited strings, silently swapping day and month whenever both are <=12 (e.g. '03/07/2026'
   becomes 7 March instead of 3 July). Detect that shape explicitly and parse it day-first via
   convertAusDate (the same parser buildFilterPayload already uses for these values); fall back to
   the generic constructor for any other shape (e.g. legacy ISO strings from bookmarked URLs
   predating dateReplacer). */
export const parseRoundTrippedDate = (value: Date | string): Date => {
	if (isDate(value)) return value as Date;
	const str = value as string;
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
		const parsed = convertAusDate(str);
		if (parsed && isDate(parsed)) return parsed;
	}
	return new Date(str);
}

export const addDays = (date: Date, amount: number): Date => addDaysFns(date, amount);

export const todayMidnight = (): Date => {
	const d = new Date();
	d.setHours(0, 0, 0, 0);
	return d;
}

export const atMidnight = (date: Date) => {
	date.setHours(0, 0, 0, 0);
	return date;
};

/* Minutes since midnight for an "HH:MM" string, for comparing times without building Dates. */
export const timeStringToMinutes = (time: string): number => {
	const [hour, min] = time.split(':').map(Number);
	return hour * 60 + min;
};

/* Inverse of timeStringToMinutes: minutes since midnight back to a zero-padded "HH:MM". */
export const minutesToTimeString = (mins: number): string => {
	const hour = Math.floor(mins / 60);
	const min = mins % 60;
	return `${`${hour}`.padStart(2, '0')}:${`${min}`.padStart(2, '0')}`;
};

export type TZoneParts = { year: number; month: number; day: number; hour: number; minute: number };

/*
	Constructing an Intl.DateTimeFormat is the expensive part, and a caller works in a handful of
	zones at most, so keep one formatter per zone rather than rebuilding it on every call.
*/
const zoneFormatters: Record<string, Intl.DateTimeFormat> = {};

const formatterFor = (tz: string): Intl.DateTimeFormat => {
	if (!zoneFormatters[tz]) {
		zoneFormatters[tz] = new Intl.DateTimeFormat('en-AU', {
			timeZone: tz,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			/* Parts are read by name, so ordering is irrelevant, but the two options below are not:
			   'h23' keeps the hour 0-23 (a 12-hour clock reports 1pm as 1, breaking any hour
			   comparison), and 'latn' keeps the digits Latin so Number() can parse them. */
			hourCycle: 'h23',
			numberingSystem: 'latn',
		});
	}
	return zoneFormatters[tz];
};

/* The calendar and clock reading of an instant in a given zone, as numbers. */
export const partsInZone = (tz: string, now: Date = new Date()): TZoneParts => {
	const fmt = formatterFor(tz);
	const parts = Object.fromEntries(
		fmt.formatToParts(now).map(({ type, value }) => [type, value])
	) as Record<string, string>;
	return {
		year: Number(parts.year),
		month: Number(parts.month),
		day: Number(parts.day),
		hour: Number(parts.hour),
		minute: Number(parts.minute),
	};
};

/* The day it currently is in a zone, as a local-midnight Date so it compares by calendar day. */
export const todayInZone = (tz: string, now: Date = new Date()): Date => {
	const { year, month, day } = partsInZone(tz, now);
	return new Date(year, month - 1, day);
};

export const isWeekend = (date: Date): boolean => {
	const day = date.getDay();
	return day === 0 || day === 6;
};

export const isSameCalendarDay = (a: Date, b: Date): boolean =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();

/*
	Calendar-day comparison, so a Date carrying a time of day still answers for its whole day.
	Builds new Dates rather than using atMidnight, which mutates what it is given.
*/
export const isBeforeDay = (date: Date, reference: Date): boolean => {
	const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const from = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
	return day.getTime() < from.getTime();
};
